import { invariant } from '@seen/core/invariant'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'

import { Button, cn } from '@seen/ui'
import { IconX } from '@tabler/icons-react'
import { type ReactNode, useMemo } from 'react'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { PractitionerOption } from '#src/components/options/PractitionerOption.tsx'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useImpersonateKnock } from '#src/hooks/useImpersonateKnock.ts'
import { usePractitionersInternal } from '#src/hooks/usePractitionersInternal.ts'
import { useRead } from '#src/hooks/useRead.ts'

export function InboxViewAs(): ReactNode {
  const currentPractitioner = useCurrentPractitioner()
  const practitioners = usePractitionersInternal()

  const impersonatedPractitionerReference = useImpersonateKnock(
    (state) => state.impersonatedPractitioner
  )

  const { data: impersonatedPractitioner } = useRead(
    ExtendedPractitioner,
    impersonatedPractitionerReference
  )

  const setImpersonatedPractitioner = useImpersonateKnock(
    (state) => state.impersonate
  )

  const options = useMemo(
    () =>
      practitioners.map((p) => ({
        value: p.reference.reference,
        label: <PractitionerOption practitioner={p.reference} />,
        keywords: p.searchKeywords,
      })),
    [practitioners]
  )

  return (
    <div className="-my-2.5 flex items-center text-foreground">
      <Combobox
        options={options}
        onSelect={(referenceString) => {
          if (referenceString === currentPractitioner?.reference.reference) {
            setImpersonatedPractitioner(null)
            return
          }
          const practitioner = practitioners.find(
            (p) => p.reference.reference === referenceString
          )
          invariant(practitioner, 'Practitioner is required')
          setImpersonatedPractitioner(practitioner.reference)
        }}
      >
        <Button
          variant="ghost"
          className={cn(!impersonatedPractitioner && '-mr-4')}
        >
          {impersonatedPractitioner && (
            <>
              Viewing as {impersonatedPractitioner.name}
              {impersonatedPractitioner.roles
                ? ` ${impersonatedPractitioner.roles.join(', ')}`
                : ''}
            </>
          )}
          {!impersonatedPractitioner && 'View as…'}
        </Button>
      </Combobox>
      {impersonatedPractitioner && (
        <Button
          variant="ghost"
          size="icon"
          className="-mr-2"
          onClick={() => setImpersonatedPractitioner(null)}
          aria-label="Stop viewing as another practitioner"
          data-dd-action-name="inbox.view_as.clear"
        >
          <IconX className="size-4" />
        </Button>
      )}
    </div>
  )
}
